import type { CsvParseResult, CsvValidationError } from "~/components/csvImport/fileUploadComponents/types";

export const DEFAULT_CSV_EXTENSIONS = [".csv"];

export const DEFAULT_MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024;

export const formatFileSize = (bytes: number): string => {
	const mb = bytes / (1024 * 1024);
	return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(1)} KB`;
};

const isValidFileSize = (file: File, maxFileSizeBytes: number): boolean => file.size <= maxFileSizeBytes;

const hasAllowedExtension = (file: File, allowedExtensions: string[]): boolean => {
	const fileName = file.name.toLowerCase();
	return allowedExtensions.some((extension) => fileName.endsWith(extension));
};

export const describeAllowedExtensions = (allowedExtensions: string[]): string =>
	allowedExtensions.map((extension) => extension.replace(".", "").toUpperCase()).join(" or ");

export const isLargeFile = (file: File): boolean => {
	const LARGE_FILE_THRESHOLD = 10 * 1024 * 1024; // 10MB warning threshold
	return file.size > LARGE_FILE_THRESHOLD;
};

export const validateCsvFile = (
	file: File,
	allowedExtensions: string[] = DEFAULT_CSV_EXTENSIONS,
	maxFileSizeBytes: number = DEFAULT_MAX_FILE_SIZE_BYTES
): CsvValidationError | null => {
	if (!hasAllowedExtension(file, allowedExtensions)) {
		return {
			field: "file",
			message: `File must be a ${describeAllowedExtensions(allowedExtensions)} file`,
			rowNumber: 0
		};
	}

	if (!isValidFileSize(file, maxFileSizeBytes)) {
		return {
			field: "file",
			message: `File size (${formatFileSize(file.size)}) exceeds maximum limit of ${formatFileSize(maxFileSizeBytes)}`,
			rowNumber: 0
		};
	}

	return null;
};

export const validateCsvDataRows = <T>(result: CsvParseResult<T>): CsvValidationError | null => {
	if (result.data.length === 0) {
		return {
			field: "file",
			message: "File must contain at least one data row",
			rowNumber: 0
		};
	}

	return null;
};
